import type { SelectOption } from '~/shared/utils/types';

import {
  defaultCapacityBounds,
  isCapacityRangeNarrowed,
  type CapacityBounds,
} from '../capacityFormat';

export type { CapacityBounds } from '../capacityFormat';
export { defaultCapacityBounds } from '../capacityFormat';

/** Pending (unsaved) state of the volume filter panel, held as one object. */
export interface VolumeFilterPanelState {
  autoscaleState: string[];
  eligibility: string[];
  accountFilters: SelectOption[];
  regionFilters: SelectOption[];
  clusterNameFilters: SelectOption[];
  nodeGroupFilters: SelectOption[];
  instanceFilters: SelectOption[];
  volumeIdFilters: SelectOption[];
  volumeTagFilters: SelectOption[];
  diskTypeFilters: SelectOption[];
  /** Capacity range, in TiB — the unit `CapacityRangeFilter` works in. */
  capacityMin: number;
  capacityMax: number;
  /**
   * The slider's endpoints, carried in the state rather than passed alongside so that
   * "is this range narrowed?" is answerable from the state alone — which is what both the
   * clear-all affordance and the API-filter builder need.
   */
  capacityBounds: CapacityBounds;
  /**
   * Whether a capacity clause is in play, tracked EXPLICITLY rather than re-derived from
   * `capacityMin/Max` vs `capacityBounds` on demand.
   *
   * Deriving it broke whenever `appliedToPanelState` widened the bounds to reach an
   * out-of-range applied handle: the widened bound lands exactly on the handle, so a range
   * seeded as `0 – 40 TiB` against a 8 TiB ceiling read as "not narrowed" and the next apply
   * dropped the filter. Both panels apply on close, so opening and closing the panel was
   * enough to lose it. See `panelState.test.ts`.
   *
   * Maintained at the two places capacity changes: seeded from the URL here, and recomputed
   * from narrowedness by `withCapacityRange` whenever the user moves a handle.
   */
  capacityFiltered: boolean;
}

/** The already-applied filters the panel seeds itself from when it opens. */
export interface AppliedVolumeFilters {
  appliedAutoscaleState: string[];
  appliedEligibility: string[];
  appliedAccountFilters: SelectOption[];
  appliedRegionFilters: SelectOption[];
  appliedClusterNameFilters: SelectOption[];
  appliedNodeGroupFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedDiskTypeFilters: SelectOption[];
  /** Undefined when the URL carries no capacity clause — the handle sits at its bound. */
  appliedCapacityMin?: number;
  appliedCapacityMax?: number;
}

export function emptyVolumeFilterPanelState(
  bounds: CapacityBounds = defaultCapacityBounds
): VolumeFilterPanelState {
  return {
    autoscaleState: [],
    eligibility: [],
    accountFilters: [],
    regionFilters: [],
    clusterNameFilters: [],
    nodeGroupFilters: [],
    instanceFilters: [],
    volumeIdFilters: [],
    volumeTagFilters: [],
    diskTypeFilters: [],
    capacityMin: bounds.min,
    capacityMax: bounds.max,
    capacityBounds: bounds,
    capacityFiltered: false,
  };
}

export function appliedToPanelState(
  applied: AppliedVolumeFilters,
  bounds: CapacityBounds = defaultCapacityBounds
): VolumeFilterPanelState {
  return {
    autoscaleState: applied.appliedAutoscaleState,
    eligibility: applied.appliedEligibility,
    accountFilters: applied.appliedAccountFilters,
    regionFilters: applied.appliedRegionFilters,
    clusterNameFilters: applied.appliedClusterNameFilters,
    nodeGroupFilters: applied.appliedNodeGroupFilters,
    instanceFilters: applied.appliedInstanceFilters,
    volumeIdFilters: applied.appliedVolumeIdFilters,
    volumeTagFilters: applied.appliedVolumeTagFilters,
    diskTypeFilters: applied.appliedDiskTypeFilters,
    // An applied handle can still sit outside the bounds — a link shared from a tenant with
    // larger volumes, or a range picked against the fallback ceiling before the real one landed.
    // Widen rather than clamp, so reopening the panel never silently loosens the user's filter.
    capacityMin: applied.appliedCapacityMin ?? bounds.min,
    capacityMax: applied.appliedCapacityMax ?? bounds.max,
    capacityBounds: {
      min: Math.min(bounds.min, applied.appliedCapacityMin ?? bounds.min),
      max: Math.max(bounds.max, applied.appliedCapacityMax ?? bounds.max),
    },
    // The URL said there is a capacity clause; that is the fact, not whatever the widened
    // bounds happen to make the handles look like.
    capacityFiltered:
      applied.appliedCapacityMin !== undefined || applied.appliedCapacityMax !== undefined,
  };
}

/**
 * Moves one or both capacity handles, re-deriving `capacityFiltered` from the result.
 *
 * Narrowedness is the right signal HERE, unlike when seeding from the URL: these are the
 * slider's own bounds, so a handle parked exactly on one genuinely means "no filter at this
 * end" — which is how clearing the section works.
 */
export function withCapacityRange(
  state: VolumeFilterPanelState,
  range: { min?: number; max?: number }
): VolumeFilterPanelState {
  const capacityMin = range.min ?? state.capacityMin;
  const capacityMax = range.max ?? state.capacityMax;
  const capacityFiltered = isCapacityRangeNarrowed(
    capacityMin,
    capacityMax,
    state.capacityBounds
  );

  // The flag is part of the comparison, not just the two numbers: clearing a range that was
  // seeded from the URL onto widened bounds moves neither handle, and must still clear the
  // flag rather than short-circuit as a no-op.
  if (
    capacityMin === state.capacityMin &&
    capacityMax === state.capacityMax &&
    capacityFiltered === state.capacityFiltered
  ) {
    return state;
  }

  return { ...state, capacityMin, capacityMax, capacityFiltered };
}

/** Whether a capacity clause should be emitted to the API. */
export function hasCapacityFilter(state: VolumeFilterPanelState): boolean {
  return state.capacityFiltered;
}

/**
 * Whether the panel's "clear all" affordance is active.
 *
 * NOTE: this reads PENDING state, not applied — matching the behaviour it replaced, where the
 * `applied*` terms were commented out. It therefore reflects unsaved edits. Pinned by the test
 * suite rather than corrected.
 */
export function panelStateHasActiveFilters(state: VolumeFilterPanelState): boolean {
  return (
    state.clusterNameFilters.length > 0 ||
    state.instanceFilters.length > 0 ||
    state.volumeIdFilters.length > 0 ||
    state.volumeTagFilters.length > 0 ||
    state.accountFilters.length > 0 ||
    state.regionFilters.length > 0 ||
    state.nodeGroupFilters.length > 0 ||
    state.diskTypeFilters.length > 0 ||
    state.autoscaleState.length > 0 ||
    state.eligibility.length > 0 ||
    hasCapacityFilter(state)
  );
}
